import React, { FC, ReactNode } from "react";
import { Input, Button, Form } from "antd";
import {
  Phase,
  PricingLevel,
  PricingType,
  OnPhasePriceChange,
  PricingSummaryFields,
  PricingSummaryRangeWithGPM,
  PricingSummary,
  PricingTypePlaceholders,
  pricingFieldsByType,
  PricingOptions,
  PhaseBase,
  EstimatedRangePricingFields,
} from "../../data-types";
import { PriceInput } from "../input-number";
import { PhaseTextEditor } from "./phase-text-editor";
import { Icons } from "../vpd-icons/icons";
import { OverridenTooltip } from "./overriden-tooltip";
import styles from "./phase-header.css";
import { useEffect } from "react";
import TextArea from "antd/lib/input/TextArea";
import { Editor } from "@tinymce/tinymce-react";
import { VpdInlineEditor } from "../tiny-text-editor/vpd-inline";
import { TimelineIndicator } from "../timeline-indicator/timeline-indicator";

const {
  ["container"]: containerClassName,
  ["form"]: formClassName,
  ["name-input"]: nameInputClassname,
  ["chosen-item"]: chosenItemClassName,
  ["chosen-group"]: chosenGroupClassName,
  calculated: calculatedClassName,
} = styles as any;

const TEXT_EDITOR_DRAG_OVERLAY_ID = Number.MIN_SAFE_INTEGER;

type SharedProps = {
  /**
   * The phase text editor is not rendered after the onDrop event triggered when sorting.
   *  After rendering in the DragOverlay the text editor fails to render in the phase header
   *  at the new position.
   *
   * Further investigation is needed to determine the root cause for the failure to render,
   *  so far it has been established that the problem is linked to how the editor is rendered
   *  based upon a specified unique id value.
   *
   * The phase id is used as the unique id for an editor.
   *
   * A workaround solution to this problme is using an alternative id value for the text editor
   *  when it is being rendered within a DragOverlay -- the **dragging** prop has been added to
   *  indicate when the alternate id should be used.
   */
  dragging?: boolean;
  template?: boolean;
  phase: Phase;
  onUpdate: (phase: PhaseBase) => void;
  onDelete: (id: number) => void;
  pricingLevel: PricingLevel;
  pricingType: PricingType;
  onPriceChange: OnPhasePriceChange;
  prefix?: ReactNode;
  currencySymbol: string;
  defaultFont?: string;
  startDate?: Date;
  deadlineDate?: Date;
  duration?: number;
  onTimelineEdit: (context: {
    id: number;
    title: string;
    startDate?: Date | null;
    deadlineDate?: Date | null;
  }) => void;
};

type AutoFocusProps = {
  autoFocus: boolean;
  onAutoFocus: () => void;
} & SharedProps;

type NoAutoFocusProps = {
  autoFocus?: false;
  onAutoFocus?: never;
} & SharedProps;

type Props<T extends boolean> = T extends true ? AutoFocusProps : NoAutoFocusProps;

const FIGURE_WIDTH = 125;
const HYPHEN_WIDTH = 30;

export const PhaseHeader = <T extends boolean>({
  dragging,
  template,
  phase: { id, title, html, description, priceOptions, work, startDate, deadlineDate },
  onUpdate,
  onDelete,
  pricingLevel,
  pricingType,
  onPriceChange,
  prefix,
  autoFocus,
  onAutoFocus,
  currencySymbol,
  defaultFont,
  onTimelineEdit,
}: Props<T>): ReturnType<FC<Props<T>>> => {
  const hasPricing = !!pricingType && pricingType !== PricingType.None;

  const pricingTypePlaceholder =
    !!pricingType && pricingType !== PricingType.None
      ? PricingTypePlaceholders[pricingType]
      : undefined;

  const workTotal = { lower: 0, upper: 0, single: 0 };
  for (const w of work ?? []) {
    workTotal.lower += w.price.lower || 0;
    workTotal.upper += w.price.upper || 0;
    workTotal.single += w.price.single || 0;
  }

  const selectedPriceSummary =
    !!pricingType && pricingType !== PricingType.None
      ? pricingFieldsByType(pricingType, priceOptions)
      : undefined;

  const isRange = hasPricing && pricingType === PricingType.EstimatedRange;
  const lowerChosenPrice = hasPricing
    ? isRange
      ? (selectedPriceSummary as PricingSummaryRangeWithGPM).chosen.lower
      : (selectedPriceSummary as PricingSummary).chosen
    : undefined;

  const upperChosenPrice = isRange
    ? (selectedPriceSummary as PricingSummaryRangeWithGPM).chosen.upper
    : undefined;

  useEffect(() => {
    autoFocus && onAutoFocus && onAutoFocus();
  }, [autoFocus, onAutoFocus]);

  return (
    <div className={containerClassName}>
      {prefix}
      <div className={formClassName}>
        <Form.Item className={nameInputClassname}>
          <VpdInlineEditor
            key={`phase-${id}`}
            initialValue={html ?? title}
            onBlur={(e) => {
              const newHtmlContent = e.target.getContent();
              const newTextContent = e.target.getContent({ format: "text" });

              if (newHtmlContent !== (html ?? title)) {
                onUpdate({
                  id,
                  title: newTextContent,
                  html: newHtmlContent,
                  description,
                });
              }
            }}
            defaultFont={defaultFont}
          />
        </Form.Item>
        {pricingLevel === PricingLevel.Phase && (
          <Form.Item className={chosenItemClassName} label="Price">
            <div className={chosenGroupClassName}>
              <PriceInput
                currencySymbol={currencySymbol}
                style={{
                  width: FIGURE_WIDTH,
                }}
                placeholder={pricingTypePlaceholder}
                onClick={(e) => e.stopPropagation()}
                defaultValue={lowerChosenPrice ?? undefined}
                step={100}
                onChange={(value) =>
                  value !== lowerChosenPrice &&
                  onPriceChange({
                    pricingType,
                    field: isRange ? PricingSummaryFields.lower : PricingSummaryFields.single,
                    value,
                    phaseId: id,
                  })
                }
                min={0}
                max={upperChosenPrice ?? undefined}
              />
              {isRange && (
                <>
                  <Input
                    style={{
                      width: HYPHEN_WIDTH,
                      borderLeft: 0,
                      borderRight: 0,
                      pointerEvents: "none",
                    }}
                    placeholder="-"
                    disabled
                  />
                  <PriceInput
                    currencySymbol={currencySymbol}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: FIGURE_WIDTH,
                    }}
                    placeholder="High"
                    defaultValue={upperChosenPrice ?? undefined}
                    step={100}
                    onChange={(value) =>
                      value !== upperChosenPrice &&
                      onPriceChange({
                        pricingType,
                        field: PricingSummaryFields.upper,
                        value,
                        phaseId: id,
                      })
                    }
                    min={lowerChosenPrice ?? undefined}
                  />
                </>
              )}
            </div>
          </Form.Item>
        )}
        {!template && pricingLevel === PricingLevel.FeeEarner && (
          <ShowOverridenTooltipIfChosenPriceSet
            priceOptions={priceOptions}
            pricingType={pricingType}
          >
            <span className={calculatedClassName}>
              <span>Calculated: </span>
              {createCalculatedText(workTotal, pricingType, currencySymbol)}
            </span>
          </ShowOverridenTooltipIfChosenPriceSet>
        )}
      </div>
      <TimelineIndicator
        isComplete={!!startDate && !!deadlineDate}
        startDate={startDate}
        deadlineDate={deadlineDate}
        onClick={(e) => {
          e.stopPropagation();
          onTimelineEdit({ id, title, startDate, deadlineDate });
        }}
      />
      <Button
        className="delete-button"
        shape="circle"
        icon={Icons.Delete}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(id);
        }}
        type="link"
      />
    </div>
  );
};

const createCalculatedText = (
  { lower, upper, single }: { lower: number; upper: number; single: number },
  pricingType: PricingType,
  currencySymbol: string
): string => {
  return pricingType === PricingType.EstimatedRange
    ? `${currencySymbol}${(lower || 0).toLocaleString()} to ${currencySymbol}${(
        upper || 0
      ).toLocaleString()}`
    : `${currencySymbol}${(single || 0).toLocaleString()}`;
};

const ShowOverridenTooltipIfChosenPriceSet: FC<{
  priceOptions: PricingOptions | null;
  pricingType: PricingType;
}> = ({ children, pricingType, priceOptions }) =>
  isPriceRangeOveridden(priceOptions, pricingType) ? (
    <OverridenTooltip>{children}</OverridenTooltip>
  ) : (
    <>{children} </>
  );

const isPriceRangeOveridden = (
  priceOptions: PricingOptions | null,
  pricingType: PricingType
): boolean => {
  switch (pricingType) {
    case PricingType.None:
      return false;

    default: {
      const transformed = pricingFieldsByType(PricingType[pricingType], priceOptions);

      if (PricingType.EstimatedRange === pricingType) {
        return (
          (transformed as EstimatedRangePricingFields).chosen.lower != null ||
          (transformed as EstimatedRangePricingFields).chosen.upper != null
        );
      }

      return transformed.chosen != null;
    }
  }
};
